import { styled } from 'styled-components';
import {
  ContentContainer,
  Title,
  Container,
  ControlsContainer,
  Text,
  Link,
  Button,
} from './Common';
import { TextFieldCompact, PasswordTextField } from '@hn-ui/textfields';
import { useState } from 'react';
import { Divider } from './Divider';
import { CleverButton } from './CleverButton';
import type { IErrorMessages } from './types';
import { publicUrls, teacherAuthUrls } from 'app/utils/urls.data';
import { MessageContainer, ErrorText, InfoContainer } from '@hn-ui/shared';
import { Warning } from '@hn-ui/icons';

export interface ILoginProps {
  cleverUrl: string;
  errors?: IErrorMessages;
}

const SignInText = styled(Text)`
  font-weight: 700;
`;

const ScInfoContainer = styled(InfoContainer)`
  align-self: flex-start;
  transform: translateY(-8px);
  margin-top: 0;
`;

const ScMessageContainer = styled(MessageContainer)`
  gap: 8px;
`;

const ErrorIcon = styled(Warning).attrs({ color: 'red50' })`
  width: 16px;
  height: 16px;
`;

const ScControlsContainer = styled(ControlsContainer)`
  transform: translateY(-28px);
`;

const TextFieldWithHiddenErrorText = styled(TextFieldCompact)`
  & > input + div,
  & > input + div > div > p {
    display: none;
  }
`;

const PasswordFieldWithHiddenErrorText = styled(PasswordTextField)`
  & > div + div + div {
    display: none;
  }
`;

const parseErrors = (errors?: IErrorMessages) => {
  if (!errors) return {};

  const createValidation = (error?: string) =>
    // clean error text to avoid multiple error messages in tests. There is only one error message in the UI.
    error ? { validate: false, errorText: '' } : undefined;

  return {
    emailValidation: createValidation(errors.email),
    firstNameValidation: createValidation(errors.firstName),
    lastNameValidation: createValidation(errors.lastName),
    passwordValidation: createValidation(errors.password),
    errorMessage:
      errors.email || errors.firstName || errors.lastName || errors.password,
  };
};

const Signup = ({ cleverUrl, errors, ...rest }: ILoginProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const isFormValid =
    email.length > 0 &&
    password.length > 0 &&
    firstName.length > 0 &&
    lastName.length > 0;

  const {
    emailValidation,
    firstNameValidation,
    lastNameValidation,
    passwordValidation,
    errorMessage,
  } = parseErrors(errors);

  return (
    <Container {...rest}>
      <Title>Create new teacher account</Title>
      <ContentContainer>
        <TextFieldWithHiddenErrorText
          placeholder="Email"
          label="email"
          name="email"
          onChange={(e) => setEmail(e.target.value.trim())}
          autofocus
          autocomplete="email"
          validation={emailValidation}
        />
        <TextFieldWithHiddenErrorText
          placeholder="Your first name"
          label="first name"
          name="first-name"
          onChange={(e) => setFirstName(e.target.value.trim())}
          autocomplete="given-name"
          validation={firstNameValidation}
        />
        <TextFieldWithHiddenErrorText
          placeholder="Your last name"
          label="last name"
          name="last-name"
          onChange={(e) => setLastName(e.target.value.trim())}
          autocomplete="family-name"
          validation={lastNameValidation}
        />
        <PasswordFieldWithHiddenErrorText
          label="password"
          placeholder="Password"
          name="password"
          onChange={(e) => setPassword(e.target.value.trim())}
          autocomplete="current-password"
          validation={passwordValidation}
        />
        <ScInfoContainer>
          {errorMessage && (
            <ScMessageContainer>
              <ErrorIcon />
              <ErrorText>{errorMessage}</ErrorText>
            </ScMessageContainer>
          )}
        </ScInfoContainer>
      </ContentContainer>
      <ScControlsContainer>
        <Button type="submit" disabled={!isFormValid}>
          Create an account
        </Button>
        <Text>
          By clicking "Create an account", you accept our{' '}
          <Link to={publicUrls.privacyPolicy}>Privacy Policy</Link> &{' '}
          <Link to={publicUrls.termsOfService}>Terms of Service</Link>
        </Text>
        <SignInText>
          Already have an account?{' '}
          <Link to={teacherAuthUrls.login} bold>
            Sign In
          </Link>
        </SignInText>
        <Divider text="OR" />
        <CleverButton
          text="Continue with Clever"
          onClick={() => (window.location.href = cleverUrl)}
        />
      </ScControlsContainer>
    </Container>
  );
};

export { Signup };
